import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, Info, MoveRight, TrendingDown, TrendingUp, UsersRound } from 'lucide-react';
import { InfoTipp, sichtbereich, zoomVon } from '../../components/Erklaerung';
import { STIL } from './cockpitTexte';
import { TippBox, type TippInhalt } from './Diagramm';
import type { Bezug, Satz, Satzteil } from './bericht';
import { trendText, zahl, type Trend } from './format';
import './bericht.css';

// Profil-Nachbau F-25 (output/profil-team-20261014/cockpit-grafiken-20261019/SCHNITTSTELLEN.md 5.5; Entwurf A „Bericht in Sätzen“):
// die Bausteine einer Seite im Stil A. Oben Satz und Folge mit unterstrichenen Bezügen, die eine Stelle der Grafik zeigen; darunter
// Blöcke mit Zahlenzeile und Vergleich (Symbol und Text, nie nur Farbe), Balkenliste mit Strich für den Vorzeitraum, Statusliste,
// Felder und der Schutz für kleine Gruppen. Die Bausteine kennen keinen Betrag, keine Person und keinen Wortlaut einer Seite.

/** Abstand des Tooltips zum Bezug und zum Rand des sichtbaren Bereichs (px). */
const ABSTAND = 6;
const RAND = 8;

/** Oben ein, zwei Sätze (Entwurf A vorschlag-a.html:15-29). Ein Bezug ist ein Knopf im Fließtext: Maus, Fokus und Antippen öffnen
 *  den Tooltip in der Gestalt des Diagramm-Tooltips (TippBox) unter dem Bezug, ohne Platz unten darüber, links an ihm ausgerichtet
 *  und nie breiter als der Bericht, und melden den Bezug (onBezug), damit die Seite die Stelle der Grafik zeigt. Maus weg, Fokus weg,
 *  Escape und ein Antippen daneben schließen und melden null. Immer nur ein Bezug offen. */
export function Bericht({ satz, folge, onBezug, tipp, label }: { satz: Satz; folge?: Satz; onBezug?: (bezug: Bezug | null) => void;
  tipp?: (bezug: Bezug) => TippInhalt | null; label?: string }) {
  const [offen, setOffen] = useState<string | null>(null);
  const offenRef = useRef<string | null>(null);
  const wurzel = useRef<HTMLElement>(null);
  const kasten = useRef<HTMLDivElement>(null);
  const knoepfe = useRef(new Map<string, HTMLButtonElement>());
  const melden = useRef(onBezug);
  melden.current = onBezug;
  const tippId = `${useId()}-tipp`;

  // Der Schlüssel eines Bezugs: s bzw. f für Satz bzw. Folge und die Stelle des Teils. Der Bezug und sein Tooltip werden bei jedem
  // Zeichnen aus den aktuellen Sätzen gelesen, damit neue Zahlen nie einen alten Tooltip stehen lassen.
  const bezugVon = (schluessel: string | null): Bezug | null => {
    if (!schluessel) return null;
    const teil = (schluessel[0] === 's' ? satz : folge ?? [])[Number(schluessel.slice(1))];
    return teil && typeof teil !== 'string' ? teil.bezug : null;
  };
  const offenerBezug = bezugVon(offen);
  const inhalt = offenerBezug ? tipp?.(offenerBezug) ?? null : null;

  const oeffnen = (schluessel: string, bezug: Bezug) => {
    if (offenRef.current === schluessel) return;
    offenRef.current = schluessel;
    setOffen(schluessel);
    melden.current?.(bezug);
  };
  const schliessen = useRef(() => {
    if (offenRef.current === null) return;
    offenRef.current = null;
    setOffen(null);
    melden.current?.(null);
  }).current;

  // Escape schließt nur den Tooltip, nicht das Fenster der Bereichsseite (SeitenRahmen hört Escape am Dokument und lässt ein
  // verbrauchtes liegen); ein Antippen daneben schließt ihn am Handy, wo ein Knopf beim Antippen keinen Fokus bekommt.
  useEffect(() => {
    if (!offen) return;
    const taste = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault(); event.stopPropagation();
      schliessen();
    };
    const daneben = (event: PointerEvent) => {
      const knopf = offenRef.current ? knoepfe.current.get(offenRef.current) : undefined;
      if (!knopf?.contains(event.target as Node)) schliessen();
    };
    document.addEventListener('keydown', taste, true);
    document.addEventListener('pointerdown', daneben, true);
    return () => {
      document.removeEventListener('keydown', taste, true);
      document.removeEventListener('pointerdown', daneben, true);
    };
  }, [offen, schliessen]);

  // Lage im Bericht (position: relative): unter der letzten Zeile des Bezugs; reicht der sichtbare Bereich unten nicht, über seiner
  // ersten Zeile. Waagrecht am Bezug, in die Breite des Berichts geklemmt.
  useLayoutEffect(() => {
    const knopf = offen ? knoepfe.current.get(offen) : undefined;
    const l = kasten.current, w = wurzel.current;
    if (!knopf || !l || !w) return;
    const basis = w.getBoundingClientRect();
    const zeilen = knopf.getClientRects();
    const erste = zeilen[0] ?? knopf.getBoundingClientRect(), letzte = zeilen[zeilen.length - 1] ?? erste;
    const bereich = sichtbereich(w);
    const z = zoomVon(l), hoehe = l.offsetHeight * z, breite = l.offsetWidth * z;
    const platzUnten = letzte.bottom + ABSTAND + hoehe <= bereich.bottom - RAND;
    const platzOben = erste.top - ABSTAND - hoehe >= bereich.top + RAND;
    const unten = platzUnten || (!platzOben && bereich.bottom - letzte.bottom >= erste.top - bereich.top);
    const anker = unten ? letzte : erste;
    const left = Math.max(0, Math.min(anker.left - basis.left, basis.width - breite));
    const top = unten ? letzte.bottom - basis.top + ABSTAND : erste.top - basis.top - ABSTAND - hoehe;
    l.style.left = `${Math.round(left / z)}px`; l.style.top = `${Math.round(top / z)}px`;
    l.dataset.seite = unten ? 'unten' : 'oben';
  });

  const teile = (zeile: Satz, vorsilbe: 's' | 'f') => zeile.map((teil: Satzteil, i) => {
    if (typeof teil === 'string') return <React.Fragment key={i}>{teil}</React.Fragment>;
    const schluessel = `${vorsilbe}${i}`;
    const sichtbar = offen === schluessel && inhalt !== null;
    return <button key={i} type="button" className="ki-bezug" data-bezug={teil.bezug.art}
      ref={knopf => { if (knopf) knoepfe.current.set(schluessel, knopf); else knoepfe.current.delete(schluessel); }}
      aria-expanded={sichtbar} aria-describedby={sichtbar ? tippId : undefined}
      onPointerEnter={() => oeffnen(schluessel, teil.bezug)}
      // Nur die Maus verlässt einen Bezug; ein Finger hebt ab (pointerleave nach pointerup), der Tooltip bleibt bis daneben.
      onPointerLeave={event => { if (event.pointerType === 'mouse') schliessen(); }}
      onFocus={() => oeffnen(schluessel, teil.bezug)}
      onBlur={schliessen}
      onClick={() => oeffnen(schluessel, teil.bezug)}>
      {teil.stark ? <strong>{teil.text}</strong> : teil.text}
    </button>;
  });

  return <section ref={wurzel} className="ki-bericht" aria-label={label ?? STIL.kurzGesagt}>
    <p className="ki-satz">{teile(satz, 's')}</p>
    {folge && <p className="ki-folge">{teile(folge, 'f')}</p>}
    {inhalt && <div ref={kasten} id={tippId} role="tooltip" className="ki-tipp"><TippBox inhalt={inhalt} /></div>}
  </section>;
}

/** Ein Block (Karte) im Stil A (Entwurf rahmen.css:200-204): Titel, Unterzeile und rechts im Kopf etwa Häkchen oder Auswahl. */
export function Block({ id, titel, unterzeile, kopf, className, children }: { id: string; titel: string; unterzeile?: string;
  kopf?: React.ReactNode; className?: string; children: React.ReactNode }) {
  return <section className={className ? `ki-block ${className}` : 'ki-block'} aria-labelledby={id}>
    <div className="ki-block-kopf">
      <div><h2 id={id} className="ki-block-titel">{titel}</h2>{unterzeile && <p className="ki-block-unterzeile">{unterzeile}</p>}</div>
      {kopf}
    </div>
    {children}
  </section>;
}

/** Vergleich zum Vorzeitraum mit Symbol und Text, nie nur Farbe (Entwurf bausteine.js:281-286): „+6 % zum Vorzeitraum“,
 *  „−11 % …“, „±0 % …“; ohne Fragen davor „neu, davor keine“, ohne Fragen in beiden Zeiträumen „– zum Vorzeitraum“. */
export function Vergleich({ trend, zusatz = true }: { trend: Trend; zusatz?: boolean }) {
  const Symbol = trend.art === 'PROZENT' && trend.prozent > 0 ? TrendingUp : trend.art === 'PROZENT' && trend.prozent < 0 ? TrendingDown : MoveRight;
  const text = trend.art === 'NEU' ? STIL.neu : `${trendText(trend)}${zusatz ? STIL.zumVorzeitraum : ''}`;
  return <span className="ki-vergleich"><Symbol size={16} aria-hidden="true" />{text}</span>;
}

export type Posten = { key: string; titel: string; wert: React.ReactNode; zusatz?: string; unter?: React.ReactNode; tipp?: React.ReactNode };
/** Die ruhige Zahlenzeile unter der Grafik (Entwurf vorschlag-a.html:36-42, 62-70). Markup wie KennzahlLeiste (Bausteine.tsx):
 *  role="group" mit Namen, je Posten div.ki-kennzahl mit Titel, Wert und small, damit die Browser-Suite die Posten weiter findet;
 *  nur das Aussehen ist neu (ki-zahlen). unter trägt etwa den Vergleich. */
export function ZahlenZeile({ label, posten }: { label: string; posten: Posten[] }) {
  return <div className="ki-kennzahlen ki-zahlen" role="group" aria-label={label} data-anzahl={posten.length}>
    {posten.map(eintrag => <div key={eintrag.key} className="ki-kennzahl">
      <span className="ki-kennzahl-kopf"><span className="ki-kennzahl-titel">{eintrag.titel}</span>{eintrag.tipp && <InfoTipp wort={eintrag.titel} text={eintrag.tipp} />}</span>
      <strong className="ki-kennzahl-wert">{eintrag.wert}</strong>
      {eintrag.zusatz && <small>{eintrag.zusatz}</small>}
      {eintrag.unter && <div className="ki-kennzahl-unter">{eintrag.unter}</div>}
    </div>)}
  </div>;
}

export type BalkenZeile = { key: string; name: string; prozent: number; wert: string; anzahl?: number | null; vorher?: number | null; neben?: boolean; markiert?: boolean };
/** Balkenzeilen (Entwurf seiten.js balken, seiten.css:57-68): Name, Balken in Prozent, Wert; mit anzahl die Zahl dahinter in Klammern
 *  („75 % (163)“, PF-SL5-woher-1); mit vorher ein Strich an der Stelle des Vorzeitraums und der Wert davor für Vorlesehilfen
 *  („davor 72 %“), nie als Zahl in Klammern. neben färbt den Balken wie die zweite Reihe (etwa „Ohne Treffer“). */
export function BalkenListe({ label, zeilen }: { label: string; zeilen: BalkenZeile[] }) {
  return <ul className="ki-balken-liste" aria-label={label}>
    {zeilen.map(zeile => {
      const vorher = typeof zeile.vorher === 'number' ? zeile.vorher : null;
      const strich = vorher === null ? 0 : Math.max(0.6, Math.min(99.4, vorher));
      return <li key={zeile.key} className={zeile.markiert ? 'ki-balken-zeile ki-markiert' : 'ki-balken-zeile'} data-key={zeile.key}>
        <span className="ki-balken-name">{zeile.name}</span>
        <svg viewBox="0 0 100 16" preserveAspectRatio="none" aria-hidden="true" focusable="false">
          <rect className="ki-balken-spur" x={0} y={2} width={100} height={12} />
          {zeile.prozent > 0 && <rect className={zeile.neben ? 'ki-d-nachfragen' : 'ki-d-recherchen'} x={0} y={2} width={Math.max(0.8, Math.min(100, zeile.prozent))} height={12} />}
          {vorher !== null && <line className="ki-balken-vorher" x1={strich} x2={strich} y1={0} y2={16} vectorEffect="non-scaling-stroke" />}
        </svg>
        <span className="ki-balken-zahl">{zeile.wert}
          {typeof zeile.anzahl === 'number' && <small> ({zahl(zeile.anzahl)})</small>}
          {vorher !== null && <span className="adm-sr-only"> {STIL.davor(vorher)}</span>}
        </span>
      </li>;
    })}
  </ul>;
}

export type StatusArt = 'hinweis' | 'ok' | 'info';
const MARKE = { hinweis: AlertTriangle, ok: CheckCircle2, info: Info } as const;
/** Statusliste (Entwurf seiten.js statusZeile, seiten.css:90-101): Marke mit Symbol und Wort, Titel und Satz, rechts eine Aktion. */
export function StatusListe({ zeilen }: { zeilen: { key: string; art: StatusArt; titel: string; text?: React.ReactNode; aktion?: React.ReactNode }[] }) {
  return <ul className="ki-status">
    {zeilen.map(zeile => {
      const Symbol = MARKE[zeile.art];
      return <li key={zeile.key}>
        <span className={`ki-marke ki-marke-${zeile.art}`}><Symbol size={20} aria-hidden="true" />{STIL.marken[zeile.art]}</span>
        <div><h3>{zeile.titel}</h3>{zeile.text && <div className="ki-status-text">{zeile.text}</div>}</div>
        {zeile.aktion ?? <span />}
      </li>;
    })}
  </ul>;
}

/** Felder (Entwurf seiten.css:40-46, 53-54): je Person oder Firma ein Feld, gefärbt nach Art, als ein Bild mit Satz für
 *  Vorlesehilfen; darunter die Legende und ein Hinweis. Mehr als 24 Felder werden niedriger. */
export function Felder({ label, felder, legende, fuss }: { label: string; felder: ('eins' | 'zwei' | 'leer')[];
  legende: { art: 'eins' | 'zwei' | 'leer'; text: string }[]; fuss?: string }) {
  const spalten = { '--ki-felder-spalten': Math.min(12, Math.max(1, felder.length)) } as React.CSSProperties;
  return <>
    <div className={felder.length > 24 ? 'ki-felder ki-felder-viele' : 'ki-felder'} role="img" aria-label={label} style={spalten}>
      {felder.map((art, i) => <span key={i} className={`ki-feld ki-feld-${art}`} />)}
    </div>
    <div className="ki-felder-text">
      {legende.map((eintrag, i) => <span key={`${eintrag.art}-${i}`}><span className={`ki-felder-schluessel ki-feld-${eintrag.art}`} aria-hidden="true" /> {eintrag.text}</span>)}
    </div>
    {fuss && <p className="ki-hinweis">{fuss}</p>}
  </>;
}

/** Schutz für kleine Gruppen (Entwurf bausteine.js:415-427, rahmen.css:213-217): statt der Zahlen ein Satz, warum, und der Weg
 *  zurück zur ganzen Firma. */
export function Schutz({ titel, text, klein, knopf, onKnopf }: { titel: string; text: string; klein: string; knopf: string; onKnopf: () => void }) {
  return <section className="ki-schutz" aria-labelledby="ki-schutz-titel">
    <UsersRound size={28} aria-hidden="true" />
    <div>
      <h2 id="ki-schutz-titel">{titel}</h2>
      <p>{text}</p>
      <p className="ki-hinweis">{klein}</p>
      <button type="button" className="adm-button adm-primary" onClick={onKnopf}>{knopf}</button>
    </div>
  </section>;
}
